import { useCallback, useEffect, useMemo } from 'react';
import _debounce from 'lodash/debounce';

type Props = {
  ref: React.RefObject<HTMLDivElement>;
  goToNextMonth: () => void;
  goToPrevMonth: () => void;
};

export const useHandlePickerKeyboardEvents = ({
  ref,
  goToNextMonth,
  goToPrevMonth,
}: Props) => {
  const handleKeyboardArrows = useCallback(
    (e: KeyboardEvent) => {
      if (['ArrowLeft', 'ArrowRight'].indexOf(e.code) > -1) {
        e.preventDefault();
        if (['ArrowLeft'].indexOf(e.code) > -1) {
          goToPrevMonth();
        } else {
          goToNextMonth();
        }
      }
    },
    [goToNextMonth, goToPrevMonth]
  );

  const handleMouseWheel = useCallback(
    (e) => {
      /**
       * YAxis scroll - mWheelUp/mWheelDown + Shift
       */
      if (e.shiftKey) {
        if (Math.sign(e.deltaY) === 1) goToNextMonth();
        if (Math.sign(e.deltaY) === -1) goToPrevMonth();
      }

      /**
       * XAxis scroll - trackpad / touchpad
       */
      if (Math.abs(e.deltaX) !== 0) {
        if (Math.sign(e.deltaX) === 1) goToNextMonth();
        if (Math.sign(e.deltaX) === -1) goToPrevMonth();
      }
    },
    [goToNextMonth, goToPrevMonth]
  );

  const debouncedMouseWheel = useMemo(
    () => _debounce(handleMouseWheel, 100),
    [handleMouseWheel]
  );

  const onMouseWheel = useCallback(
    (e) => {
      if (e.shiftKey || e.deltaX !== 0) {
        e.preventDefault();
        e.stopPropagation();
        debouncedMouseWheel(e);
      }
    },
    [debouncedMouseWheel]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboardArrows);
    if (ref.current) {
      ref.current.addEventListener('mousewheel', onMouseWheel, {
        passive: false,
      });
    }
    return () => {
      window.removeEventListener('keydown', handleKeyboardArrows);
      if (ref.current) {
        ref.current.removeEventListener('mousewheel', onMouseWheel);
      }
    };
  }, [handleKeyboardArrows, onMouseWheel]);
};
